import { cn } from '@/lib/utils';

type BarTone = 'emerald' | 'sky' | 'amber' | 'slate';

const toneClassMap: Record<BarTone, string> = {
  emerald: 'bg-emerald-500 dark:bg-emerald-400',
  sky: 'bg-sky-500 dark:bg-sky-400',
  amber: 'bg-amber-500 dark:bg-amber-400',
  slate: 'bg-slate-500 dark:bg-slate-400',
};

export interface BarListDatum {
  key: string;
  label: string;
  valueLabel: string;
  percentageLabel: string;
  percentage: number;
  hint?: string;
  tone?: BarTone;
}

export interface BarListProps {
  data: BarListDatum[];
  className?: string;
  barClassName?: string;
  toneOverride?: BarTone;
}

const MIN_VISIBLE_WIDTH = 4;

export const BarList = ({ data, className, barClassName, toneOverride }: BarListProps): JSX.Element => {
  return (
    <div className={cn('space-y-3', className)}>
      {data.map((item) => {
        const tone = toneOverride ?? item.tone ?? 'emerald';
        const barWidth = item.percentage > 0 ? Math.max(MIN_VISIBLE_WIDTH, item.percentage * 100) : 0;
        return (
          <div key={item.key} className="space-y-1">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{item.label}</p>
                {item.hint ? (
                  <p className="text-xs text-muted-foreground" aria-hidden="true">
                    {item.hint}
                  </p>
                ) : null}
              </div>
              <div className="shrink-0 text-sm font-semibold text-foreground">{item.valueLabel}</div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-muted">
                <span
                  className={cn('absolute left-0 top-0 h-full rounded-full transition-all', toneClassMap[tone], barClassName)}
                  style={{ width: barWidth > 0 ? `${barWidth}%` : '0%' }}
                  aria-hidden="true"
                />
              </div>
              <span className="shrink-0 font-medium tracking-tight text-foreground/75">{item.percentageLabel}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
